import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ReloadOutlined,
    UnorderedListOutlined,
    ClockCircleOutlined,
    ClearOutlined,
} from "@ant-design/icons";
import {
    Button,
    Card,
    Table,
    Tag,
    message,
    Modal,
    Input,
    Space,
    Tooltip,
    Select,
    DatePicker,
    Row,
    Col,
    Typography,
} from "antd";
import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { NavigationContext } from "../../context/NavigationContext";
import dayjs from "dayjs";

const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

export default function StatusUpdation() {
    const { authData, backUrl } = useContext(AuthContext);
    const { navigationParams, clearNavigationParams } = useContext(NavigationContext);
    const isMobile = window.innerWidth < 768;

    // State management
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // ✅ Active tab state: "pending" | "approved" | "rejected"
    const [activeTab, setActiveTab] = useState("pending");

    // ✅ Filter reference data
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    // ✅ Filter states — text filters match the backend's icontains fields
    const [employeeNameFilter, setEmployeeNameFilter] = useState("");
    const [empCodeFilter, setEmpCodeFilter] = useState("");
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedDesignations, setSelectedDesignations] = useState([]);
    const [dateRange, setDateRange] = useState([null, null]);

    // Modal state for approval/rejection
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [actionType, setActionType] = useState(""); // "approve" or "reject"
    const [remarks, setRemarks] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // ✅ Tab configuration — colors, labels, and which endpoint each tab hits
    const tabConfig = {
        pending: {
            label: "Pending",
            color: "#faad14",       // yellow
            bgActive: "#faad14",
            icon: <ClockCircleOutlined />,
            endpoint: "pending-list",
        },
        approved: {
            label: "Approved",
            color: "#52c41a",       // green
            bgActive: "#52c41a",
            icon: <CheckCircleOutlined />,
            endpoint: "all-history",
        },
        rejected: {
            label: "Rejected",
            color: "#ff4d4f",       // red
            bgActive: "#ff4d4f",
            icon: <CloseCircleOutlined />,
            endpoint: "all-history",
        },
    };

    // Status color mapping
    const getStatusColor = (status) => {
        const statusColors = {
            "PRESENT": "green",
            "PERMISSION": "orange",
            "ABSENT": "red",
            "HOLIDAY": "blue",
            "Weekoff": "purple",
            "Compoff": "cyan",
            "CASUAL LEAVE": "magenta",
            "SICK LEAVE": "volcano",
            "HALFDAY_LEAVE": "geekblue",
        };
        return statusColors[status] || "default";
    };

    // ✅ Fetch reference data for department/designation dropdowns
    const fetchDepartments = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setDepartments(res.data.results || []);
        } catch (err) {
            console.error("Failed to load departments");
        }
    };

    const fetchDesignations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setDesignations(res.data.results || []);
        } catch (err) {
            console.error("Failed to load designations");
        }
    };

    useEffect(() => {
        fetchDepartments();
        fetchDesignations();
    }, []);

    // ✅ Fetch requests — endpoint & params driven by active tab, filters sent to backend
    const fetchRequests = async () => {
        setLoading(true);
        try {
            const { endpoint } = tabConfig[activeTab];
            const params = new URLSearchParams();
            params.append('page', currentPage);
            params.append('page_size', pageSize);

            // Approved/Rejected both use all-history; outcome splits them
            if (activeTab === 'approved') params.append('outcome', 'approved');
            if (activeTab === 'rejected') params.append('outcome', 'rejected');

            if (employeeNameFilter.trim()) {
                params.append('employee_name', employeeNameFilter.trim());
            }
            if (empCodeFilter.trim()) {
                params.append('emp_code', empCodeFilter.trim());
            }
            if (selectedDepartments.length > 0) {
                selectedDepartments.forEach((deptId) => {
                    params.append('department', deptId);
                });
            }
            if (selectedDesignations.length > 0) {
                selectedDesignations.forEach((desgId) => {
                    params.append('designation', desgId);
                });
            }
            if (dateRange && dateRange[0] && dateRange[1]) {
                params.append('from_date', dayjs(dateRange[0]).format('YYYY-MM-DD'));
                params.append('to_date', dayjs(dateRange[1]).format('YYYY-MM-DD'));
            }

            const url = `${backUrl}/api/hr/attendance-changes/${endpoint}/?${params.toString()}`;
            console.log("Fetching requests from:", url);

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            console.log("Backend Response:", res.data);

            // pending-list / all-history are paginated (results/count);
            // my-requests-style {data, total_count} isn't used here, but guard anyway
            const data = res.data.results || res.data.data || res.data;
            setDataSource(data);
            setTotalRecords(res.data.count ?? res.data.total_count ?? data.length);
        } catch (error) {
            if (error.response?.status === 404) {
                // Page no longer has data (e.g., filters changed, page moved) — reset to page 1
                setCurrentPage(1);
                return;
            }
            console.error("Error fetching requests:", error);
            message.error(`Failed to fetch ${activeTab} status change requests`);
            setDataSource([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    // Initial load & Navigation Sync
    useEffect(() => {
        if (navigationParams?.status) {
            const navStatus = navigationParams.status.toLowerCase();
            if (["pending", "approved", "rejected"].includes(navStatus)) {
                setActiveTab(navStatus);
                setCurrentPage(1);
            }
            clearNavigationParams();
        }
    }, [navigationParams, clearNavigationParams]);

    // ✅ Refetch whenever tab, page, page size, or any filter changes
    useEffect(() => {
        fetchRequests();
    }, [
        currentPage,
        pageSize,
        activeTab,
        employeeNameFilter,
        empCodeFilter,
        selectedDepartments,
        selectedDesignations,
        dateRange,
    ]);

    // Handle table pagination change
    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    // ✅ Handle tab click
    const handleTabClick = (tabKey) => {
        if (tabKey === activeTab) return;
        setActiveTab(tabKey);
        setCurrentPage(1); // reset to first page on tab switch
    };

    // ✅ Filter change handlers (each resets to page 1)
    const handleEmployeeNameChange = (e) => {
        setEmployeeNameFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleEmpCodeChange = (e) => {
        setEmpCodeFilter(e.target.value);
        setCurrentPage(1);
    };

    const handleDepartmentChange = (value) => {
        setSelectedDepartments(value);
        setCurrentPage(1);
    };

    const handleDesignationChange = (value) => {
        setSelectedDesignations(value);
        setCurrentPage(1);
    };

    const handleDateRangeChange = (dates) => {
        setDateRange(dates || [null, null]);
        setCurrentPage(1);
    };

    const handleClearAllFilters = () => {
        setEmployeeNameFilter("");
        setEmpCodeFilter("");
        setSelectedDepartments([]);
        setSelectedDesignations([]);
        setDateRange([null, null]);
        setCurrentPage(1);
    };

    const hasActiveFilters = () => {
        return (
            employeeNameFilter.trim() ||
            empCodeFilter.trim() ||
            selectedDepartments.length > 0 ||
            selectedDesignations.length > 0 ||
            (dateRange && dateRange[0] && dateRange[1])
        );
    };

    // Open approval/rejection modal
    const handleActionClick = (record, action) => {
        setSelectedRequest(record);
        setActionType(action);
        setRemarks("");
        setIsModalVisible(true);
    };

    // Submit approval or rejection
    const handleSubmitAction = async () => {
        if (!selectedRequest) return;

        if (!remarks.trim()) {
            message.warning(`Please enter a reason for ${actionType === "approve" ? "approval" : "rejection"}`);
            return;
        }

        setSubmitting(true);
        try {
            const endpoint = actionType === "approve"
                ? `${backUrl}/api/hr/attendance-changes/${selectedRequest.id}/approve/`
                : `${backUrl}/api/hr/attendance-changes/${selectedRequest.id}/reject/`;

            const payload = {
                reason: remarks.trim()
            };

            await axios.post(endpoint, payload, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            message.success(
                `Status change request ${actionType === "approve" ? "approved" : "rejected"} successfully!`
            );

            setIsModalVisible(false);
            setRemarks("");
            setSelectedRequest(null);
            setActionType("");
            fetchRequests();
            setCurrentPage(1);
        } catch (error) {
            console.error(`Error ${actionType}ing request:`, error);

            const errorMessage =
                error.response?.data?.detail ||
                error.response?.data?.message ||
                error.response?.data?.error ||
                `Failed to ${actionType} status change request`;

            message.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    // Handle modal cancel
    const handleModalCancel = () => {
        setIsModalVisible(false);
        setRemarks("");
        setSelectedRequest(null);
        setActionType("");
    };

    // ✅ Base columns shown for all tabs
    const baseColumns = [
        {
            title: 'Requested By',
            dataIndex: "requested_by_name",
            key: 'requested_by'
        },
        {
            title: "Employee Code",
            dataIndex: "emp_code",
            key: "emp_code",
            width: 180,
        },
        {
            title: "Employee Name",
            dataIndex: "employee_name",
            key: "employee_name",
            width: 180,
        },
        {
            title: "Department",
            dataIndex: "department_name",
            key: "department",
            width: 150,
        },
        {
            title: "Designation",
            dataIndex: "designation_name",
            key: "designation",
            width: 150,
        },
        {
            title: "Attendance Date",
            dataIndex: "date",
            key: "date",
            width: 130,
        },
        {
            title: "Current Status",
            dataIndex: "status",
            key: "current_status",
            width: 130,
        },
        {
            title: "Requested Status",
            dataIndex: "pending_status_change",
            key: "new_status",
        },
        {
            title: "Remarks",
            dataIndex: "status_change_remarks",
            key: "remarks",
            width: 200,
        },
    ];

    // ✅ Extra columns depending on tab
    const pendingActionColumn = {
        title: "Action",
        key: "action",
        width: 180,
        render: (_, record) => {
            return (
                <Space size="small">
                    <Button
                        type="primary"
                        icon={<CheckCircleOutlined />}
                        onClick={() => handleActionClick(record, "approve")}
                        style={{
                            backgroundColor: "#52c41a",
                            borderColor: "#52c41a",
                        }}
                        shape="round"
                    >
                    </Button>
                    <Button
                        danger
                        icon={<CloseCircleOutlined />}
                        onClick={() => handleActionClick(record, "reject")}
                        shape="round"
                    >
                    </Button>
                </Space>
            )
        },
    };

    // ✅ Approved/Rejected tabs show the superuser's remarks + a tag instead of actions
    const resolvedStatusColumn = {
        title: "Reviewer Remarks",
        dataIndex: "superuser_remarks",
        key: "superuser_remarks",
        width: 220,
        render: (val) => val || "-",
    };

    const columns = activeTab === "pending"
        ? [...baseColumns, pendingActionColumn]
        : [...baseColumns, resolvedStatusColumn];

    return (
        <section>
            <center>
                {isMobile ? (
                    <p style={{ fontSize: "15px", textTransform: "uppercase", color: "#0e497b" }}>
                        Status Change Requests
                    </p>
                ) : (
                    <h5>
                        Attendance Status Change Requests
                    </h5>
                )}
            </center>

            {/* ✅ Filter Section */}
            <div style={{ margin: "0 20px" }}>
                <Card style={{ marginBottom: "16px" }}>
                    <Row gutter={[16, 16]} align="bottom">
                        <Col xs={24} sm={12} md={8} lg={5}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Employee Name</Text>
                                <Input
                                    placeholder="Search by name"
                                    value={employeeNameFilter}
                                    onChange={handleEmployeeNameChange}
                                    allowClear
                                />
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={4}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Employee Code</Text>
                                <Input
                                    placeholder="Search by code"
                                    value={empCodeFilter}
                                    onChange={handleEmpCodeChange}
                                    allowClear
                                />
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={5}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Department</Text>
                                <Select
                                    mode="multiple"
                                    placeholder="Select departments"
                                    value={selectedDepartments}
                                    onChange={handleDepartmentChange}
                                    style={{ width: "100%" }}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {departments.map((dept) => (
                                        <Option key={dept.id} value={dept.id}>
                                            {dept.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={5}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Designation</Text>
                                <Select
                                    mode="multiple"
                                    placeholder="Select designations"
                                    value={selectedDesignations}
                                    onChange={handleDesignationChange}
                                    style={{ width: "100%" }}
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {designations.map((desg) => (
                                        <Option key={desg.id} value={desg.id}>
                                            {desg.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Space>
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={5}>
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                <Text strong>Attendance Date Range</Text>
                                <RangePicker
                                    value={dateRange}
                                    onChange={handleDateRangeChange}
                                    format="DD-MM-YYYY"
                                    placeholder={["Start Date", "End Date"]}
                                    style={{ width: "100%" }}
                                />
                            </Space>
                        </Col>

                        {hasActiveFilters() && (
                            <Col xs={24} sm={12} md={8} lg={4}>
                                <Button
                                    icon={<ClearOutlined />}
                                    onClick={handleClearAllFilters}
                                    style={{ width: "100%" }}
                                >
                                    Clear Filters
                                </Button>
                            </Col>
                        )}
                    </Row>
                </Card>
            </div>

            {/* ✅ Tab Buttons — Pending / Approved / Rejected */}
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "12px",
                    margin: "16px 20px 0",
                    flexWrap: "wrap",
                }}
            >
                {Object.keys(tabConfig).map((tabKey) => {
                    const tab = tabConfig[tabKey];
                    const isActive = activeTab === tabKey;
                    return (
                        <Button
                            key={tabKey}
                            icon={tab.icon}
                            onClick={() => handleTabClick(tabKey)}
                            style={{
                                minWidth: "130px",
                                fontWeight: 600,
                                color: isActive ? "#fff" : tab.color,
                                backgroundColor: isActive ? tab.bgActive : "#fff",
                                borderColor: tab.color,
                            }}
                        >
                            {tab.label}
                        </Button>
                    );
                })}
            </div>

            {/* Requests Table */}
            <div style={{ margin: "20px" }}>
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    bordered
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalRecords,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                    }}
                    scroll={{ x: "max-content" }}
                    rowKey={(record) => record.id}
                    onChange={handleTableChange}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            </div>

            {/* Approval/Rejection Modal */}
            <Modal
                title={actionType === "approve" ? "Approve Status Change" : "Reject Status Change"}
                open={isModalVisible}
                onOk={handleSubmitAction}
                onCancel={handleModalCancel}
                confirmLoading={submitting}
                okText={actionType === "approve" ? "Approve" : "Reject"}
                okButtonProps={{
                    danger: actionType === "reject",
                    style: actionType === "approve"
                        ? { backgroundColor: "#52c41a", borderColor: "#52c41a" }
                        : undefined,
                }}
                cancelText="Cancel"
                width={600}
            >
                {selectedRequest && (
                    <div>
                        <div style={{ marginBottom: "20px" }}>
                            <p>
                                <strong>Employee:</strong>{" "}
                                {selectedRequest.employee_name} ({selectedRequest.emp_code})
                            </p>
                            <p>
                                <strong>Attendance Date:</strong>{" "}
                                {dayjs(selectedRequest.date).format("DD/MM/YYYY")}
                            </p>
                            <p>
                                <strong>Current Status:</strong>{" "}
                                <Tag color={getStatusColor(selectedRequest.status)}>
                                    {selectedRequest.status}
                                </Tag>
                            </p>
                            <p>
                                <strong>Requested Status:</strong>{" "}
                                <Tag color={getStatusColor(selectedRequest.pending_status_change)}>
                                    {selectedRequest.pending_status_change}
                                </Tag>
                            </p>
                            <p>
                                <strong>Request Remarks:</strong>{" "}
                                {selectedRequest.status_change_remarks || "-"}
                            </p>
                            <p>
                                <strong>Requested By:</strong>{" "}
                                {selectedRequest.requested_by_name}
                            </p>
                        </div>

                        <div>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: "500",
                                }}
                            >
                                {actionType === "approve" ? "Approval Reason:" : "Rejection Reason:"}{" "}
                                <span style={{ color: "red" }}>*</span>
                            </label>
                            <TextArea
                                rows={4}
                                placeholder={
                                    actionType === "approve"
                                        ? "Enter reason for approval..."
                                        : "Enter reason for rejection..."
                                }
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                maxLength={500}
                                showCount
                            />
                        </div>
                    </div>
                )}
            </Modal>
        </section>
    );
}